import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import type { Call } from "@sales-ops-amplify/shared";
import {
  createCallShare,
  markCallInterested,
  updateCall,
} from "@/api/calls";
import { logFollowup } from "@/api/followups";

type Props = {
  call: Call;
  onCallUpdated?: (call: Call) => void;
  compact?: boolean;
};

export function CallActionsMenu({ call, onCallUpdated, compact }: Props) {
  const [open, setOpen] = useState(false);
  const [followUpOpen, setFollowUpOpen] = useState(false);
  const [nextFollowUp, setNextFollowUp] = useState("");
  const [message, setMessage] = useState<string | null>(null);

  const shareMut = useMutation({
    mutationFn: () => createCallShare(call.callId),
    onSuccess: async (res) => {
      onCallUpdated?.(res.call);
      const url = `${window.location.origin}${res.sharePath}`;
      try {
        await navigator.clipboard.writeText(url);
        setMessage("Share link copied");
      } catch {
        setMessage(url);
      }
      setTimeout(() => setMessage(null), 2500);
      setOpen(false);
    },
  });

  const interestedMut = useMutation({
    mutationFn: (interested: boolean) =>
      markCallInterested(call.callId, interested),
    onSuccess: (res) => {
      onCallUpdated?.(res.call);
      setMessage(res.call.interested ? "Marked interested" : "Cleared interested");
      setTimeout(() => setMessage(null), 2000);
      setOpen(false);
    },
  });

  const followUpMut = useMutation({
    mutationFn: () =>
      logFollowup({
        company: call.accountId ?? call.leadName ?? "Unknown",
        dealName: call.leadName ?? `Call ${call.callId.slice(0, 8)}`,
        opportunityId: call.opportunityId,
        leadId: call.leadId,
        leadName: call.leadName,
        accountId: call.accountId,
        channel: "call",
        notes:
          call.notes ||
          call.aiSummary ||
          call.insights?.outcome ||
          "Follow-up from call",
        nextFollowUp: nextFollowUp
          ? new Date(nextFollowUp).toISOString()
          : undefined,
      }),
    onSuccess: () => {
      setMessage("Follow-up logged");
      setFollowUpOpen(false);
      setOpen(false);
      setTimeout(() => setMessage(null), 2000);
    },
  });

  const downloadMut = useMutation({
    mutationFn: async () => {
      if (!call.recordingUrl) throw new Error("No recording");
      const a = document.createElement("a");
      a.href = call.recordingUrl;
      a.download = `call-${call.callId}.mp3`;
      a.target = "_blank";
      a.rel = "noreferrer";
      a.click();
    },
  });

  const notesMut = useMutation({
    mutationFn: () =>
      updateCall(call.callId, {
        notes: [
          call.notes,
          call.insights?.followUpEmailDraft
            ? `\n--- Follow-up draft ---\nSubject: ${call.insights.followUpEmailDraft.subject}\n${call.insights.followUpEmailDraft.bodyText}`
            : null,
        ]
          .filter(Boolean)
          .join("\n"),
      }),
    onSuccess: (res) => {
      onCallUpdated?.(res.call);
      setMessage("Draft appended to notes");
      setTimeout(() => setMessage(null), 2000);
      setOpen(false);
    },
  });

  return (
    <div className="relative inline-block text-left">
      <button
        type="button"
        className={
          compact
            ? "rounded border border-border px-1.5 py-0.5 text-xs"
            : "rounded-md border border-border px-2.5 py-1.5 text-sm"
        }
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
      >
        Actions ▾
      </button>
      {open && (
        <div className="absolute right-0 z-20 mt-1 w-52 rounded-md border border-border bg-surface py-1 shadow-lg">
          <button
            type="button"
            className="block w-full px-3 py-1.5 text-left text-sm hover:bg-surface-hover"
            onClick={() => shareMut.mutate()}
          >
            Copy share link
          </button>
          <button
            type="button"
            className="block w-full px-3 py-1.5 text-left text-sm hover:bg-surface-hover"
            onClick={() => interestedMut.mutate(!call.interested)}
          >
            {call.interested ? "Clear interested" : "Mark interested"}
          </button>
          <button
            type="button"
            className="block w-full px-3 py-1.5 text-left text-sm hover:bg-surface-hover"
            onClick={() => setFollowUpOpen(true)}
          >
            Create follow-up…
          </button>
          {call.recordingUrl && (
            <button
              type="button"
              className="block w-full px-3 py-1.5 text-left text-sm hover:bg-surface-hover"
              onClick={() => downloadMut.mutate()}
            >
              Download recording
            </button>
          )}
          {call.insights?.followUpEmailDraft && (
            <button
              type="button"
              className="block w-full px-3 py-1.5 text-left text-sm hover:bg-surface-hover"
              onClick={() => notesMut.mutate()}
            >
              Append email draft to notes
            </button>
          )}
          <a
            href={`/conversations/dialer?callId=${encodeURIComponent(call.callId)}`}
            className="block w-full px-3 py-1.5 text-left text-sm hover:bg-surface-hover"
            onClick={() => setOpen(false)}
          >
            Open in Conversations
          </a>
        </div>
      )}

      {followUpOpen && (
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/30 p-4">
          <div className="w-full max-w-sm space-y-3 rounded-lg border border-border bg-surface p-4 shadow-xl">
            <h3 className="font-medium">Create follow-up</h3>
            <p className="text-xs text-muted-foreground">
              Logs a follow-up for {call.leadName || call.accountId || "this call"}.
            </p>
            <label className="block text-xs">
              Next follow-up date
              <input
                type="date"
                className="mt-1 w-full rounded-md border border-border bg-background px-2 py-1.5 text-sm"
                value={nextFollowUp}
                onChange={(e) => setNextFollowUp(e.target.value)}
              />
            </label>
            <div className="flex justify-end gap-2">
              <button
                type="button"
                className="rounded-md border border-border px-3 py-1.5 text-sm"
                onClick={() => setFollowUpOpen(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="rounded-md bg-brand px-3 py-1.5 text-sm text-white"
                disabled={followUpMut.isPending}
                onClick={() => followUpMut.mutate()}
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}

      {message && (
        <p className="absolute right-0 top-full mt-1 whitespace-nowrap text-xs text-brand">
          {message}
        </p>
      )}
    </div>
  );
}
